/**
 * Forms Page
 *
 * Displays form documents filtered from the documents list.
 * Split layout: document list on left, form sending component on right.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormsListBlock from '@components/Patient/Blocks/FormsListBlock';
import PatientPicker from '@components/Patient/PatientPicker';
import { EntitlementGate, EntitlementUsage } from '@features/entitlements';
import { GUIDE_SURFACES, GuideLink, PageGuideLink } from '@features/knowledge-base/links';
import { useSubjectLanguageVariants } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import { Avatar } from '@jotpsych/ui';
import {
  Assignment as FormIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  LibraryBooks as LibraryIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { Patient } from '../../../types/types';
import { type SortField, useDocumentsList } from '../hooks/useDocumentsList';
import { useLazyGetDocumentProxyBlobQuery } from '../store/api';
import type { DocumentSummaryResponse } from '../types';

import { DocumentDetailModal } from './DocumentDetailModal';
import DocumentFilters from './DocumentFilters';
import { DocumentsEmptyState } from './DocumentsEmptyState';

// Format date to localized string
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const FormsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { singularCap: subjectLanguageCap, singular: subjectLanguage } =
    useSubjectLanguageVariants();
  const { toolbar: toolbarMetrics, document: documentMetrics } = useEventTracking();

  // Document download hook - fetch blob directly via proxy
  const [fetchDocumentBlob] = useLazyGetDocumentProxyBlobQuery();

  // Track page view
  const hasTrackedPageView = useRef(false);

  // Document detail modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Form sending state
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Mobile full-screen form view state
  const [isMobileFormViewOpen, setIsMobileFormViewOpen] = useState(false);

  // Use the documents list hook with fixed form filter
  const {
    documents,
    isLoading,
    isFetching,
    error,
    page,
    pageSize,
    totalRows,
    filters,
    sortBy,
    sortOrder,
    hasActiveFilters,
    setPage,
    setPageSize,
    setPatientId,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    clearFilters,
    refresh,
  } = useDocumentsList({ fixedDocumentType: 'form', syncWithUrl: false });

  // Fetch patient when filter patientId is set (for syncing with form picker)
  const { patient: filterPatient } = usePatientById(filters.patientId || undefined);

  // Sync filter patient to form patient when filter changes
  useEffect(() => {
    if (
      filterPatient &&
      (!selectedPatient || selectedPatient.patient_id !== filterPatient.patient_id)
    ) {
      setSelectedPatient(filterPatient);
    } else if (!filters.patientId && selectedPatient) {
      // Clear form patient when filter is cleared
      setSelectedPatient(null);
    }
  }, [filterPatient, filters.patientId, selectedPatient]);

  // Track page view once documents are loaded
  useEffect(() => {
    if (!isLoading && !hasTrackedPageView.current) {
      documentMetrics.pageViewed({
        page_type: 'forms',
        document_count: totalRows,
        has_filters: false, // Forms page has fixed filter
      });
      hasTrackedPageView.current = true;
    }
  }, [isLoading, totalRows, documentMetrics]);

  // Handle row click to open detail modal
  const handleRowClick = useCallback(
    (params: GridRowParams<DocumentSummaryResponse>) => {
      const doc = params.row;
      // Track row click
      documentMetrics.rowClicked({
        document_id: doc.document_id,
        document_type: 'form',
        page_type: 'forms',
        has_patient: !!doc.patient_id,
      });
      setSelectedDocumentId(doc.document_id);
      setIsDetailModalOpen(true);
    },
    [documentMetrics],
  );

  // Handle view button click
  const handleViewClick = useCallback(
    (event: React.MouseEvent, document: DocumentSummaryResponse) => {
      event.stopPropagation();
      setSelectedDocumentId(document.document_id);
      setIsDetailModalOpen(true);
    },
    [],
  );

  // Handle download button click
  const handleDownloadClick = useCallback(
    async (event: React.MouseEvent, doc: DocumentSummaryResponse) => {
      event.stopPropagation();
      try {
        // Fetch blob directly via RTK Query proxy endpoint (auth handled automatically)
        const blob = await fetchDocumentBlob({
          documentId: doc.document_id,
          disposition: 'attachment',
        }).unwrap();

        // Track download action
        documentMetrics.downloaded({
          document_id: doc.document_id,
          document_type: 'form',
          page_type: 'forms',
          file_size: doc.size_bytes,
        });

        const blobUrl = URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = doc.filename || `${doc.document_title || 'document'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL after a short delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('Download error:', error);
        enqueueSnackbar('Failed to download document', { variant: 'error' });
      }
    },
    [fetchDocumentBlob, documentMetrics, enqueueSnackbar],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  // Handle pagination model change
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const newPage = model.page + 1;
      const newPageSize = model.pageSize;

      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      } else if (newPage !== page) {
        setPage(newPage);
      }
      // Track pagination change
      const calculatedTotalPages = totalRows > 0 ? Math.ceil(totalRows / newPageSize) : 0;
      documentMetrics.paginationChanged({
        new_page: newPage,
        rows_per_page: newPageSize,
        total_pages: calculatedTotalPages,
        source: 'forms_page',
      });
    },
    [setPage, setPageSize, page, pageSize, totalRows, documentMetrics],
  );

  // Handle sort model change
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        const fieldMap: Record<string, SortField> = {
          document_title: 'filename',
          filename: 'filename',
          created_at: 'created_at',
        };
        const apiField = fieldMap[field] ?? 'created_at';
        setSortBy(apiField);
        setSortOrder(sort ?? 'desc');
        // Track sort change
        documentMetrics.sortChanged({
          sort_field: apiField,
          sort_order: sort ?? 'desc',
          page_type: 'forms',
        });
      } else {
        setSortBy('created_at');
        setSortOrder('desc');
        documentMetrics.sortChanged({
          sort_field: 'created_at',
          sort_order: 'desc',
          page_type: 'forms',
        });
      }
    },
    [setSortBy, setSortOrder, documentMetrics],
  );

  // Handle patient selection (for form/mobile view)
  const handlePatientSelect = useCallback(
    (patient: Patient | undefined) => {
      const patientId = patient?.patient_id || null;
      setSelectedPatient(patient || null);
      // Sync with filter patient picker
      setPatientId(patientId);
      // Refresh document list when patient is selected (forms might be sent)
      if (patient) {
        refresh();
      }
    },
    [refresh, setPatientId],
  );

  // Handle mobile form view open
  const handleMobileFormViewOpen = useCallback(() => {
    setIsMobileFormViewOpen(true);
    setSelectedPatient(null);
    // Track mobile view opened
    documentMetrics.mobileViewOpened({ page_type: 'forms' });
    toolbarMetrics.actionClicked({
      action_type: 'send_forms',
      has_patient: false,
    });
  }, [toolbarMetrics, documentMetrics]);

  // Handle mobile form view close
  const handleMobileFormViewClose = useCallback(() => {
    // Track mobile view closed
    documentMetrics.mobileViewClosed({
      page_type: 'forms',
      had_patient_selected: !!selectedPatient,
    });
    setIsMobileFormViewOpen(false);
    setSelectedPatient(null);
  }, [documentMetrics, selectedPatient]);

  // Define columns
  const columns: GridColDef<DocumentSummaryResponse>[] = useMemo(
    () => [
      {
        field: 'document_title',
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        valueGetter: (value, row) => row.document_title || row.filename,
        renderCell: params => (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', overflow: 'hidden' }}>
            <FormIcon color="primary" fontSize="small" />
            <Typography variant="body2" noWrap title={params.value as string}>
              {params.value as string}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'patient',
        headerName: subjectLanguageCap,
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        renderCell: params => {
          const patient = params.row.patient;
          const patientId = params.row.patient_id;

          if (!patient && !patientId) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          const firstName = patient?.first_name || '';
          const lastName = patient?.last_name || '';
          const displayName =
            patient?.name ||
            patient?.preferred_name ||
            (firstName && lastName
              ? `${firstName} ${lastName}`
              : firstName || lastName || 'Unknown');

          return (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', overflow: 'hidden' }}>
              {patientId && (
                <Avatar
                  id={patientId}
                  firstName={firstName || undefined}
                  lastName={lastName || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant="body2" noWrap title={displayName}>
                {displayName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 130,
        valueFormatter: value => formatDate(value),
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={e => handleDownloadClick(e, params.row)}
                aria-label="Download document"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={e => handleViewClick(e, params.row)}
                aria-label="View document details"
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleViewClick, handleDownloadClick, subjectLanguageCap],
  );

  // Sort model for DataGrid
  const sortModel: GridSortModel = useMemo(
    () => [{ field: sortBy === 'filename' ? 'document_title' : sortBy, sort: sortOrder }],
    [sortBy, sortOrder],
  );

  // Pagination model
  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page: page - 1, pageSize }),
    [page, pageSize],
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
        gap: 3,
      }}
    >
      {/* Page Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <FormIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                Forms
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View and manage form documents
            </Typography>
            <PageGuideLink page="forms" />
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<LibraryIcon />}
              onClick={() => navigate('/forms')}
              sx={{ textTransform: 'none' }}
            >
              Forms Library
            </Button>
            {isMobile && !isMobileFormViewOpen && (
              <Button
                variant="contained"
                startIcon={<FormIcon />}
                onClick={handleMobileFormViewOpen}
                sx={{ textTransform: 'none' }}
              >
                Send Form
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Entitlement Usage */}
      {!isMobileFormViewOpen && (
        <EntitlementUsage capabilityName="form_sending" source="forms_page" />
      )}

      {/* Error Alert */}
      {error && !isMobileFormViewOpen && (
        <Box sx={{ flexShrink: 0 }}>
          <Chip label={error} color="error" variant="outlined" />
        </Box>
      )}

      {/* Filters */}
      {!isMobileFormViewOpen && (
        <Box sx={{ flexShrink: 0 }}>
          <DocumentFilters
            filters={filters}
            onPatientIdChange={setPatientId}
            onDocumentTypeChange={() => {}} // Disabled - fixed to form
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            isLoading={isLoading || isFetching}
            hideDocumentTypeFilter={true}
          />
        </Box>
      )}

      {isMobileFormViewOpen ? (
        // Mobile full-screen form view
        <EntitlementGate
          capabilityName="form_sending"
          source="forms_page_mobile_panel"
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleMobileFormViewClose}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                zIndex: 1001,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'background.paper',
                },
              }}
              aria-label="Close form view"
            >
              <CloseIcon />
            </IconButton>

            {/* Header with Patient Picker */}
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Send Forms to {subjectLanguageCap}
              </Typography>
              <PatientPicker
                onPatientSelect={handlePatientSelect}
                initialPatient={selectedPatient || undefined}
                size="small"
              />
            </Box>

            {/* Forms List Block */}
            {selectedPatient ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <FormsListBlock
                  patientId={selectedPatient.patient_id}
                  patient={selectedPatient}
                  defaultCollapsed={false}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <FormIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a {subjectLanguageCap}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ px: 4 }}>
                  Choose a {subjectLanguageCap.toLowerCase()} above to view and manage forms
                </Typography>
              </Box>
            )}
          </Box>
        </EntitlementGate>
      ) : (
        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, maxHeight: '100%' }}>
          {/* Left Column - Document List */}
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                '& .MuiDataGrid-root': {
                  border: 'none',
                  height: '100%',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}
            >
              <DataGrid
                rows={documents}
                columns={columns}
                getRowId={row => row.document_id}
                loading={isLoading || isFetching}
                pagination
                paginationMode="server"
                rowCount={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 20, 50]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                onRowClick={handleRowClick}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                sx={{
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '52px',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <DocumentsEmptyState
                      icon={FormIcon}
                      message="No forms found"
                      secondaryAction={
                        !hasActiveFilters && (
                          <GuideLink
                            surfaceId={GUIDE_SURFACES.FORMS_EMPTY_STATE}
                            shape="secondary-action"
                            topic="forms"
                          />
                        )
                      }
                    />
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Right Column - Form Sending Component */}
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}
          >
            {!isMobile && (
              <EntitlementGate
                capabilityName="form_sending"
                source="forms_page_panel"
                sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                overlaySx={{ borderRadius: 2 }}
              >
                <Paper
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Patient Picker */}
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Send Forms to {subjectLanguageCap}
                    </Typography>
                    <PatientPicker
                      onPatientSelect={handlePatientSelect}
                      initialPatient={selectedPatient || undefined}
                      size="small"
                    />
                  </Box>

                  {/* Content Area */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      p: 2,
                    }}
                  >
                    {!selectedPatient ? (
                      // No patient selected - show prompt
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <FormIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Select a {subjectLanguageCap}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ px: 4 }}>
                          Choose a {subjectLanguageCap.toLowerCase()} above to view and manage forms
                        </Typography>
                      </Box>
                    ) : (
                      // Patient selected - show FormsListBlock
                      <FormsListBlock
                        patientId={selectedPatient.patient_id}
                        patient={selectedPatient}
                        defaultCollapsed={false}
                      />
                    )}
                  </Box>
                </Paper>
              </EntitlementGate>
            )}
          </Grid>
        </Grid>
      )}

      {/* Document Detail Modal */}
      <DocumentDetailModal
        open={isDetailModalOpen}
        documentId={selectedDocumentId}
        onClose={handleCloseModal}
      />
    </Box>
  );
};

export default FormsPage;
